import type { MaybeRefOrGetter } from 'vue'
import { SITE_URL } from '~/utils/schemaOrg'

export const HREFLANG_MAP = {
  es: 'es-ES',
  en: 'en-US',
  pt: 'pt-PT',
  zh: 'zh-CN',
  fr: 'fr-FR',
} as const

type LocaleCode = keyof typeof HREFLANG_MAP

/**
 * Emit `<link rel="canonical">` and `<link rel="alternate" hreflang>` for the
 * current page. `path` is the unprefixed route (e.g. '/shop' or a ref/getter
 * returning `/blog/tag/foo`); locale prefixes are added automatically.
 * x-default points to Spanish (the default locale).
 *
 * The locale prefix is constructed manually rather than via switchLocalePath
 * to match the pattern in producto/[slug].vue — switchLocalePath occasionally
 * returns a stale path on dynamic routes before params hydrate. We assume the
 * `prefix_except_default` i18n strategy with no route aliasing (the only
 * config used here).
 *
 * Pass `noindex: true` for private/transactional pages where we want hreflang
 * suppressed and a robots noindex meta emitted.
 */
export function useSeoCanonical(
  path: MaybeRefOrGetter<string>,
  options: {
    noindex?: MaybeRefOrGetter<boolean>
    canonicalQuery?: MaybeRefOrGetter<Record<string, string | undefined>>
  } = {},
) {
  const localePath = useLocalePath()

  const resolvedPath = computed(() => toValue(path))

  const queryString = computed(() => {
    if (!options.canonicalQuery) return ''
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(toValue(options.canonicalQuery))) {
      if (value) params.set(key, value)
    }
    const serialized = params.toString()
    return serialized ? `?${serialized}` : ''
  })

  const canonicalUrl = computed(() => `${SITE_URL}${localePath(resolvedPath.value)}${queryString.value}`)

  const hreflangLinks = computed(() => {
    if (options.noindex && toValue(options.noindex)) return []
    const p = resolvedPath.value
    const codes = Object.keys(HREFLANG_MAP) as LocaleCode[]
    const pathFor = (code: LocaleCode) =>
      code === 'es' ? p : `/${code}${p === '/' ? '' : p}`
    const links = codes.map(code => ({
      rel: 'alternate' as const,
      hreflang: HREFLANG_MAP[code] as string,
      href: `${SITE_URL}${pathFor(code)}${queryString.value}`,
    }))
    links.push({
      rel: 'alternate' as const,
      hreflang: 'x-default',
      href: `${SITE_URL}${p === '/' ? '/' : p}${queryString.value}`,
    })
    return links
  })

  if (options.noindex) {
    useSeoMeta({ robots: () => toValue(options.noindex!) ? 'noindex, follow' : 'index, follow' })
  }

  useHead({
    link: () => [
      { rel: 'canonical', href: canonicalUrl.value },
      ...hreflangLinks.value,
    ],
  })

  return { canonicalUrl, hreflangLinks }
}
